/**
 * SettingsProfilePage
 * Landlord self-service profile editor.
 * Route: /settings/profile
 */

import { useState, useEffect } from 'react';
import {
  getManagerProfile,
  updateManagerProfile,
  type ManagerProfile,
  type UpdateProfileInput,
  ApiClientError,
} from '../lib/api';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SettingsLayout } from '../components/settings/SettingsLayout';

export function SettingsProfilePage(): JSX.Element {
  const [profile, setProfile] = useState<ManagerProfile | null>(null);
  const [draft, setDraft] = useState<UpdateProfileInput>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getManagerProfile();
        if (!cancelled) {
          setProfile(data);
          setDraft({
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            address1: data.address1,
            address2: data.address2,
            city: data.city,
            state: data.state,
            zipCode: data.zipCode,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load profile');
          console.error(err);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasChanges =
    profile !== null &&
    (draft.firstName !== profile.firstName ||
      draft.lastName !== profile.lastName ||
      draft.phone !== profile.phone ||
      draft.address1 !== profile.address1 ||
      draft.address2 !== profile.address2 ||
      draft.city !== profile.city ||
      draft.state !== profile.state ||
      draft.zipCode !== profile.zipCode);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!hasChanges || isSaving) return;
    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const updated = await updateManagerProfile(draft);
      setProfile(updated);
      setDraft({
        firstName: updated.firstName,
        lastName: updated.lastName,
        phone: updated.phone,
        address1: updated.address1,
        address2: updated.address2,
        city: updated.city,
        state: updated.state,
        zipCode: updated.zipCode,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to save profile');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="px-[var(--page-padding-x)]" data-testid="settings-profile-loading">
          <div className="h-7 w-48 bg-muted animate-pulse rounded mb-2" />
          <div className="h-4 w-72 bg-muted animate-pulse rounded" />
        </div>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout>
      <div data-testid="settings-profile-page">
        <div className="px-[var(--page-padding-x)]">
          <PageHeader
            title="Profile"
            subtitle="Manage your personal contact information."
            className="mb-6"
          />

          {error && (
            <div
              data-testid="profile-error-message"
              className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 mb-6"
              role="alert"
            >
              <p className="text-sm text-destructive m-0">{error}</p>
            </div>
          )}

          {saveSuccess && (
            <div
              data-testid="profile-save-success"
              className="rounded-lg border border-success/30 bg-success/5 p-3 mb-6"
              role="status"
            >
              <p className="text-sm text-success m-0">
                Profile saved successfully.
              </p>
            </div>
          )}

          <form onSubmit={handleSave} noValidate data-testid="profile-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* First Name */}
              <div>
                <label
                  htmlFor="profile-first-name"
                  className="text-sm font-medium text-foreground block mb-1.5"
                >
                  First Name
                </label>
                <Input
                  id="profile-first-name"
                  data-testid="profile-first-name-input"
                  value={draft.firstName ?? ''}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, firstName: e.target.value || null }))
                  }
                />
              </div>

              {/* Last Name */}
              <div>
                <label
                  htmlFor="profile-last-name"
                  className="text-sm font-medium text-foreground block mb-1.5"
                >
                  Last Name
                </label>
                <Input
                  id="profile-last-name"
                  data-testid="profile-last-name-input"
                  value={draft.lastName ?? ''}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, lastName: e.target.value || null }))
                  }
                />
              </div>
            </div>

            {/* Email (read-only) */}
            <div className="mb-4">
              <label
                htmlFor="profile-email"
                className="text-sm font-medium text-foreground block mb-1.5"
              >
                Email
              </label>
              <Input
                id="profile-email"
                data-testid="profile-email-input"
                value={profile?.email ?? ''}
                readOnly
                disabled
                aria-describedby="profile-email-note"
              />
              <p id="profile-email-note" className="text-xs text-muted-foreground mt-1">
                Email cannot be changed here.
              </p>
            </div>

            {/* Phone */}
            <div className="mb-6">
              <label
                htmlFor="profile-phone"
                className="text-sm font-medium text-foreground block mb-1.5"
              >
                Phone
              </label>
              <Input
                id="profile-phone"
                data-testid="profile-phone-input"
                value={draft.phone ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, phone: e.target.value || null }))
                }
              />
            </div>

            {/* Address */}
            <section aria-labelledby="profile-address-heading" className="mb-6">
              <h2
                id="profile-address-heading"
                className="text-sm font-semibold text-foreground mb-3"
              >
                Address
              </h2>
              <div className="flex flex-col gap-4">
                <div>
                  <label
                    htmlFor="profile-address1"
                    className="text-sm font-medium text-foreground block mb-1.5"
                  >
                    Address Line 1
                  </label>
                  <Input
                    id="profile-address1"
                    data-testid="profile-address1-input"
                    value={draft.address1 ?? ''}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, address1: e.target.value || null }))
                    }
                  />
                </div>

                <div>
                  <label
                    htmlFor="profile-address2"
                    className="text-sm font-medium text-foreground block mb-1.5"
                  >
                    Address Line 2{' '}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <Input
                    id="profile-address2"
                    data-testid="profile-address2-input"
                    value={draft.address2 ?? ''}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, address2: e.target.value || null }))
                    }
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-1">
                    <label
                      htmlFor="profile-city"
                      className="text-sm font-medium text-foreground block mb-1.5"
                    >
                      City
                    </label>
                    <Input
                      id="profile-city"
                      data-testid="profile-city-input"
                      value={draft.city ?? ''}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, city: e.target.value || null }))
                      }
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="profile-state"
                      className="text-sm font-medium text-foreground block mb-1.5"
                    >
                      State
                    </label>
                    <Input
                      id="profile-state"
                      data-testid="profile-state-input"
                      value={draft.state ?? ''}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, state: e.target.value || null }))
                      }
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="profile-zip"
                      className="text-sm font-medium text-foreground block mb-1.5"
                    >
                      ZIP Code
                    </label>
                    <Input
                      id="profile-zip"
                      data-testid="profile-zip-input"
                      value={draft.zipCode ?? ''}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, zipCode: e.target.value || null }))
                      }
                    />
                  </div>
                </div>
              </div>
            </section>

            <Button
              type="submit"
              disabled={!hasChanges || isSaving}
              data-testid="profile-save-button"
              className="min-h-[44px]"
            >
              {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save changes'}
            </Button>
          </form>
        </div>
      </div>
    </SettingsLayout>
  );
}

export default SettingsProfilePage;
